import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  Wrench,
  Headphones,
  Box,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileText,
  Mail,
  Phone,
  Globe,
  Award
} from 'lucide-react';

const WarrantyInformation = () => {
  const topFeatures = [
    {
      title: '2 Year Coverage',
      desc: 'All InfraEdge devices are covered for 2 years from the date of purchase.',
      icon: ShieldCheck
    },
    {
      title: 'Manufacturing Defects',
      desc: 'Covers any defects in materials or workmanship under normal use during the warranty period.',
      icon: Wrench
    },
    {
      title: 'Dedicated Support',
      desc: 'Our support team is here to help you with any warranty related queries.',
      icon: Headphones
    },
    {
      title: 'Fast Resolution',
      desc: 'We aim to repair or replace eligible items quickly and hassle-free.',
      icon: Box
    }
  ];

  const whatsCovered = [
    'Manufacturing defects in materials and workmanship',
    'LED performance and functionality',
    'Power supply and electrical components',
    'Control panel and remote (if applicable)',
    'Normal use under operating guidelines'
  ];

  const whatsNotCovered = [
    'Damage caused by misuse, abuse, or improper installation',
    'Accidental damage, drops, or impacts',
    'Damage from unauthorized repairs or modifications',
    'Damage caused by power surges or voltage issues',
    'Normal wear and tear or cosmetic changes'
  ];

  const claimSteps = [
    {
      num: '1',
      title: 'Contact Support',
      desc: 'Reach out to our support team via email or phone with your order details.'
    },
    {
      num: '2',
      title: 'Provide Information',
      desc: 'Share product photos/video and a description of the issue.'
    },
    {
      num: '3',
      title: 'Assessment',
      desc: 'Our team will review and confirm warranty eligibility.'
    },
    {
      num: '4',
      title: 'Resolution',
      desc: 'We will repair or replace the product or affected part as per our warranty policy.'
    }
  ];

  return (
    <div style={{ background: '#fafbfc', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#0f172a', padding: '40px 24px 80px', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '1140px', margin: '0 auto' }}>
        
        {/* Breadcrumbs */}
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, marginBottom: '24px' }}>
          <Link to="/" style={{ color: '#64748b', textDecoration: 'none' }}>Home</Link>
          <span style={{ margin: '0 8px' }}>›</span>
          <span style={{ color: '#0f172a', fontWeight: 700 }}>Warranty Information</span>
        </div>

        {/* HERO HEADER WITH 2 YEAR WARRANTY BADGE */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '32px', alignItems: 'center', marginBottom: '48px' }}>
          <div>
            <h1 style={{ fontSize: '44px', fontWeight: 900, color: '#0f172a', lineHeight: 1.1, marginBottom: '16px', letterSpacing: '-0.02em' }}>
              Warranty Information
            </h1>
            <div style={{ width: '40px', height: '3px', background: '#dc2626', borderRadius: '2px', marginBottom: '20px' }} />
            <p style={{ fontSize: '15px', color: '#475569', lineHeight: 1.7, maxWidth: '520px', margin: 0 }}>
              We stand behind the quality of our InfraEdge products. All InfraEdge devices come with a comprehensive 2 Year Warranty for your peace of mind.
            </p>
          </div>

          {/* Right 2 Year Warranty Emblem & Badges */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px' }}>
            <div style={{ position: 'relative', width: '180px', height: '180px', background: '#dc2626', borderRadius: '30px 30px 90px 90px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#ffffff', boxShadow: '0 16px 36px rgba(220,38,38,0.25)', border: '4px solid #ffffff' }}>
              <div style={{ fontSize: '56px', fontWeight: 900, lineHeight: 1 }}>2</div>
              <div style={{ fontSize: '13px', fontWeight: 900, letterSpacing: '0.1em' }}>YEAR</div>
              <div style={{ background: '#0f172a', color: '#ffffff', fontSize: '11px', fontWeight: 900, padding: '4px 14px', borderRadius: '9999px', marginTop: '6px', letterSpacing: '0.08em' }}>
                WARRANTY
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>
                <CheckCircle2 size={18} color="#dc2626" />
                <span>Premium Quality</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>
                <CheckCircle2 size={18} color="#dc2626" />
                <span>Reliable Performance</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>
                <CheckCircle2 size={18} color="#dc2626" />
                <span>Peace of Mind</span>
              </div>
            </div>
          </div>
        </div>

        {/* 4 TOP FEATURE CARDS */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '48px' }}>
          {topFeatures.map((feat, idx) => {
            const Icon = feat.icon;
            return (
              <div key={idx} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '24px 20px', display: 'flex', gap: '16px', alignItems: 'flex-start', boxShadow: '0 4px 16px rgba(0,0,0,0.02)' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={22} />
                </div>
                <div>
                  <h3 style={{ fontSize: '14.5px', fontWeight: 900, color: '#0f172a', marginBottom: '4px' }}>{feat.title}</h3>
                  <p style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.5, margin: 0 }}>{feat.desc}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* 3 COLUMNS: WHAT'S COVERED | WHAT'S NOT COVERED | HOW TO MAKE A CLAIM */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '48px' }}>
          
          {/* Column 1: What's Covered */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '32px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 4px 16px rgba(0,0,0,0.02)' }}>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a', marginBottom: '20px' }}>What's Covered</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '24px' }}>
                {whatsCovered.map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <CheckCircle2 size={18} color="#16a34a" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '13px', color: '#475569', lineHeight: 1.5, fontWeight: 600 }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <ShieldCheck size={18} color="#16a34a" />
              <span style={{ fontSize: '12px', color: '#15803d', fontWeight: 800 }}>Coverage valid for 2 years from date of purchase.</span>
            </div>
          </div>

          {/* Column 2: What's Not Covered */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '32px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 4px 16px rgba(0,0,0,0.02)' }}>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a', marginBottom: '20px' }}>What's Not Covered</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '24px' }}>
                {whatsNotCovered.map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <XCircle size={18} color="#dc2626" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '13px', color: '#475569', lineHeight: 1.5, fontWeight: 600 }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '12px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <AlertTriangle size={18} color="#dc2626" />
              <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 800 }}>For complete details, please refer to our Warranty Terms.</span>
            </div>
          </div>

          {/* Column 3: How to Make a Warranty Claim */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '32px 24px', boxShadow: '0 4px 16px rgba(0,0,0,0.02)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a', marginBottom: '20px' }}>How to Make a Warranty Claim</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {claimSteps.map((step) => (
                <div key={step.num} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#dc2626', color: '#ffffff', fontSize: '12px', fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                    {step.num}
                  </div>
                  <div>
                    <h4 style={{ fontSize: '13.5px', fontWeight: 900, color: '#0f172a', margin: '0 0 2px 0' }}>{step.title}</h4>
                    <p style={{ fontSize: '12px', color: '#64748b', margin: 0, lineHeight: 1.5 }}>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* BOTTOM IMPORTANT NOTES & NEED HELP BANNER */}
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '20px', padding: '28px 36px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '24px' }}>
          
          {/* Left Important Notes */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', maxWidth: '580px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#ffffff', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
              <FileText size={22} />
            </div>
            <div>
              <h4 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', margin: '0 0 6px 0' }}>Important Notes</h4>
              <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12.5px', color: '#475569', lineHeight: 1.6, fontWeight: 600 }}>
                <li>Warranty is valid only for the original purchaser.</li>
                <li>Proof of purchase (order number or invoice) is required.</li>
                <li>Warranty is non-transferable.</li>
              </ul>
            </div>
          </div>

          {/* Right Contact Details */}
          <div>
            <h4 style={{ fontSize: '15px', fontWeight: 900, color: '#0f172a', margin: '0 0 10px 0' }}>Need Help?</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <a href="mailto:Info@infraedgeredlight.co.uk" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#dc2626', fontSize: '13px', fontWeight: 700, textDecoration: 'none' }}>
                <Mail size={15} />
                <span>Info@infraedgeredlight.co.uk</span>
              </a>
              <a href="tel:01214556529" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '13px', fontWeight: 700, textDecoration: 'none' }}>
                <Phone size={15} color="#dc2626" />
                <span>0121 455 6529</span>
              </a>
              <a href="https://activeacademy.co.uk/red-light-infrared-therapy" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '13px', fontWeight: 700, textDecoration: 'none' }}>
                <Globe size={15} color="#dc2626" />
                <span>www.activeacademy.co.uk</span>
              </a>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default WarrantyInformation;
